/* import { tracked } from '@glimmer/tracking'; */
import { next } from '@ember/runloop';
import { service } from '@ember/service';

import type {
	StyleParams as FocusOverlayOffsetParams,
	PopupInnerComponentClass,
	Service,
} from 'webapp-types';
import { AttachPosition, Direction } from 'webapp/enums/enums';
import type { TrackingEvent } from 'webapp/enums/tracking';
import { isElementOnScreen } from 'webapp/utils/html';

import CommonPopupsPopupBlueBubble from 'webapp/components/common/popups/popup-blue-bubble';

import type PopupContext from 'webapp/models-custom/popup/context';
import Serviced from 'webapp/models-custom/serviced';

export interface StepData {
	step?: number;
	direction: Direction;
	sourceName?: string | null;
	el?: JQuery<HTMLElement> | null;
	allowStacked?: boolean;
	attachTo?: () => JQuery<HTMLElement> | null;
	attachPosition?: AttachPosition;
	scrollToAttachTo?: boolean;
	doAction?: (data: number | Event) => void; // step | event
	focusOverlay?: {
		show: boolean;
		offset?: FocusOverlayOffsetParams;
		el?: () => JQuery<HTMLElement>;
	};
}

// a step with its attachTo and focus overlay elements looked up (getPropsForStep)
type ResolvedStepData = Omit<StepData, 'attachTo' | 'focusOverlay'> & {
	attachTo?: JQuery<HTMLElement> | null;
	focusOverlay?: Omit<NonNullable<StepData['focusOverlay']>, 'el'> & { el?: HTMLElement };
};

type ExtraOptions = Record<string, unknown>; // assigned onto the tour instance

export default abstract class Tour extends Serviced {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('tour') declare tourService: Service.Tour;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('flow/tour/reports-pageless')
	declare flowTourReportsPagelessService: Service.Flow.Tour.ReportsPageless;
	@service('flow/tour/reports-slides')
	declare flowTourReportsSlidesService: Service.Flow.Tour.ReportsSlides;

	isActive = false;

	currentStep = 0;
	trackEvent: TrackingEvent | '' = '';

	popupComponent: PopupInnerComponentClass = CommonPopupsPopupBlueBubble;

	extraClass = '';
	currentPopup: PopupContext | null = null;

	// Extra options passed while initializing Tour
	el: JQuery<HTMLElement> | null = null;
	sourceName: string | null = null;

	// To be overriden in classes that extends Tour
	get targetElement(): JQuery<HTMLElement> | null {
		return null;
	}

	// To be overriden in classes that extends Tour
	get stepData(): StepData[] {
		return [];
	}

	// To be overriden in classes that extends Tour
	// for saving in user-settings table
	get keyForPersistantDone(): string {
		return '';
	}

	abstract get id(): string;

	constructor(opts?: ExtraOptions) {
		super();

		if (opts) {
			for (const [key, value] of Object.entries(opts)) {
				(this as ExtraOptions)[key] = value;
			}
		}
	}

	// we added an optional parameter, so that we could start the tour at a specific step
	// and also for a workaround for goToStep() method not repositioning the bubble (from Popups V3 update)
	start(startingStep?: number): void {
		/*
			Some tours extend this class and have their own start methods that open different popups than the blue bubble.
			In them, this.popupName is set to an empty string, which means that when they call super.start(); the goToStep method will skip opening the blue bubble.
			The other popups that can be open by a Tour are described in < export const TourPopup >
		*/
		this.isActive = true;
		this.goToStep(startingStep || 1);

		const trackEvent = this.trackEvent;
		if (trackEvent) {
			this.analyticsService.event(trackEvent);
		}
	}

	end(): void {
		if (this.isActive) {
			this.markDone();
			this.isActive = false;
		}

		this.hideBubble();
	}

	reset(): void {
		const key = this.keyForPersistantDone;
		if (key) {
			this.markPersistant('reset', key);
		}
	}

	markDone(): void {
		const key = this.keyForPersistantDone;
		if (key) {
			this.markPersistant('done', key);
		}
	}

	markPersistant(_status: string, _key: string): void {
		// defined in tour service.
	}

	repositionBubble(): void {
		this.currentPopup?.reposition();
	}

	hideBubble(): void {
		if (this.currentPopup) {
			void this.currentPopup.close();

			this.currentPopup = null;
		}
	}

	getPropsForStep(step: number): ResolvedStepData {
		const { attachTo, focusOverlay, ...stepData } = { ...this.stepData[step - 1]! };
		const props: ResolvedStepData = stepData;

		if (attachTo) {
			// execute to get the element only when we need it
			props.attachTo = attachTo();
		}

		if (focusOverlay) {
			const { el, ...overlay } = focusOverlay;
			props.focusOverlay = el ? { ...overlay, el: el().get(0) } : overlay;
		}

		return props;
	}

	goToStep(step: number): void {
		const props = this.getPropsForStep(step);
		if (!this.isActive || !this.checkIfStepHasValidAttachTo(props)) {
			return;
		}

		const resolvedStepProps = this.getPropsForStep(step);
		const stepProps = {
			...resolvedStepProps,
			attachTo: resolvedStepProps.attachTo!.get(0)!,
			attachPosition: resolvedStepProps.attachPosition || AttachPosition.CENTER,
			type: this.id,
			onExplicitClose: () => {
				this.onExplicitClose();
			},
		};

		const { direction, allowStacked, attachPosition, ...popupProps } = stepProps;

		if (this.currentPopup) {
			// Another step of the same type
			this.hideBubble();
		}

		const popup = this.popupService.createContext(this.popupComponent, stepProps.attachTo, {
			popupClass: 'popup-type-blue-bubble' + (this.extraClass ? ' ' + this.extraClass : ''),
			closeOnOutsideClickInModal: false,
			theme: 'blue',
			allowStacked,
			directions: [
				direction,
				...[Direction.DOWN, Direction.RIGHT, Direction.UP, Direction.LEFT].filter(
					(d) => d !== direction,
				),
			],
			attachPosition,
		});

		next(() => {
			popup.open(popupProps);

			if (popup) {
				this.currentPopup = popup;
				this.repositionBubble();
				this.currentStep = step;
			}

			if (stepProps.scrollToAttachTo && !isElementOnScreen(stepProps.attachTo)) {
				stepProps.attachTo.scrollIntoView(); // scroll to element where blue bubble should be attached
			}
		});
	}

	checkIfStepHasValidAttachTo(props: ResolvedStepData): boolean {
		return !!props.attachTo && props.attachTo.length !== 0 && props.attachTo.is(':visible');
	}

	onExplicitClose(): void {
		this.end();
	}
}
